import { z } from 'zod'
import { addCustomIssue } from 'utils/schema.util'
import { isFilled, parseBoolean, validateMeasuresApplied } from 'utils/validate.util'
import { SystemMessages } from 'utils/enums/system-message.enum'
import { creasSchema } from 'types/creas.type'
import { AnexoSchema } from 'types/socioeducation-measure-procedural-data.type'
import dayjs from 'dayjs'

export const SocioeducationMeasureMeasuresAppliedLaSchema = z
  .object({
    ativo: z.boolean(),
    periodicidadeAcompanhamento: z.string().nullish(),
    atividadesIndividuaisColetivas: z.string().nullish(),
  })
  .superRefine((fields, ctx) => {
    const addIssue = (field: string, message: string) => addCustomIssue(ctx, [field], message)

    if (fields.ativo) {
      if (!isFilled(fields.periodicidadeAcompanhamento)) {
        addIssue('periodicidadeAcompanhamento', SystemMessages.MESSAGE_002)
      }
    }
  })

export const SocioeducationMeasureMeasuresAppliedPscSchema = z
  .object({
    ativo: z.boolean(),
    habilidadePotencialidades: z.string().nullish(),
    aguardandoArticulacao: z.string().nullish(),
    localCumprimento: creasSchema.nullish(),
    profissionalReferencia: z.string().nullish(),
    cargaHoraria: z.string().nullish(),
    tempoDeterminado: z.string().nullish(),
    principaisAtividades: z.string().nullish(),
    sugestaoExtincao: z.string().nullish(),
    dificuldadeAdesao: z.string().nullish(),
    recusaAdolescenteCumprimento: z.string().nullish(),
  })
  .superRefine((fields, ctx) => {
    const addIssue = (field: string, message: string) => addCustomIssue(ctx, [field], message)
    if (fields.ativo) {
      if (fields.aguardandoArticulacao === 'S') {
        if (!isFilled(fields.localCumprimento)) {
          addIssue('localCumprimento', SystemMessages.MESSAGE_002)
        }

        if (!isFilled(fields.profissionalReferencia)) {
          addIssue('profissionalReferencia', SystemMessages.MESSAGE_002)
        }

        if (!isFilled(fields.cargaHoraria)) {
          addIssue('cargaHoraria', SystemMessages.MESSAGE_002)
        }

        if (!isFilled(fields.tempoDeterminado)) {
          addIssue('tempoDeterminado', SystemMessages.MESSAGE_002)
        }

        if (!isFilled(fields.principaisAtividades)) {
          addIssue('principaisAtividades', SystemMessages.MESSAGE_002)
        }
      }
    }
  })

export const MeasuresAppliedSchema = z
  .object({
    ordem: z.number().nullish(),
    la: z.object({
      dataInicioCumprimento: z.string().nullish(),
    }),
    psc: z.object({
      dataInicioCumprimento: z.string().nullish(),
    }),
    meioAberto: z.object({
      id: z.number().nullish(),
      status: z.string().nullish(),
    }),
    dataEntregaRelatorio: z.string().nullish(),
    anexo: AnexoSchema,
  })
  .superRefine((fields, ctx) => {
    const addIssue = (field: string, message: string) => addCustomIssue(ctx, [field], message)

    if (isFilled(fields.dataEntregaRelatorio) && !isFilled(fields.anexo?.arquivoBase64)) {
      addIssue('anexo.arquivoBase64', SystemMessages.MESSAGE_002)
    }

    if (
      isFilled(fields.dataEntregaRelatorio) &&
      (isFilled(fields.la?.dataInicioCumprimento) || isFilled(fields.psc?.dataInicioCumprimento))
    ) {
      const dataInicio =
        dayjs(fields.la?.dataInicioCumprimento).isValid() &&
        isFilled(fields.la?.dataInicioCumprimento)
          ? dayjs(fields.la?.dataInicioCumprimento)
          : dayjs(fields.psc?.dataInicioCumprimento)
      if (dayjs(fields.dataEntregaRelatorio).isBefore(dataInicio)) {
        addIssue('dataEntregaRelatorio', SystemMessages.MESSAGE_DATE_IS_GREATER)
      }
    }
  })

export const SocioeducationMeasureMeasuresAppliedSchema = z.object({
  id: z.number().nullish(),
  medidasAplicadas: z.array(MeasuresAppliedSchema),
  la: SocioeducationMeasureMeasuresAppliedLaSchema,
  psc: SocioeducationMeasureMeasuresAppliedPscSchema,
  observacoes: z.string().nullish(),
})

export type MeasuresAppliedType = z.infer<typeof MeasuresAppliedSchema>

export type SocioeducationMeasureMeasuresAppliedType = z.infer<
  typeof SocioeducationMeasureMeasuresAppliedSchema
>

export const SocioeducationMeasureMeasuresAppliedDefaultValues: SocioeducationMeasureMeasuresAppliedType =
  {
    medidasAplicadas: [],
    la: {
      ativo: false,
    },
    psc: {
      ativo: false,
    },
  }

export const MeasuresAppliedDefaultValues: MeasuresAppliedType = {
  ordem: 0,
  anexo: {},
  psc: {},
  la: {},
  meioAberto: {},
}
